/** The client side of sign-in: who am I, how do I leave. */

import type { MotionPreference } from './lib/motion'

export type SessionUser = {
  id: string
  email: string
  displayName: string
  isAdmin: boolean
  planId: string | null
  themeId: string | null
  /** How much this account wants the app to move. `src/lib/motion.ts` is the rule. */
  motion: MotionPreference
}

export type SessionOk = { status: 'ok'; user: SessionUser | null }
export type SessionError = { status: 'error'; error: string }
export type SessionState = SessionOk | SessionError

/** The answer to asking for an emailed link: it was sent, or it was refused. */
export type EmailLinkSent = { status: 'ok'; email: string; expiresInMinutes: number }
export type EmailLinkState = EmailLinkSent | SessionError

/**
 * Where the "Sign in with Google" link goes. It is a plain link, not a fetch:
 * the browser has to follow the redirect to Google itself.
 */
export function signInUrl(next = '/'): string {
  return `/api/auth/google/start?next=${encodeURIComponent(next)}`
}

async function post(path: string, fetchImpl: typeof fetch): Promise<SessionState> {
  try {
    const res = await fetchImpl(path, { method: 'POST' })
    const body = (await res.json()) as { status?: string; error?: string }
    if (body?.status === 'ok') return { status: 'ok', user: null }
    return { status: 'error', error: body?.error ?? `HTTP ${res.status}` }
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Who this browser is, or `user: null` if nobody. Never throws. The session
 * cookie rides along on its own: these are same-origin requests, which fetch
 * sends credentials with by default.
 */
export async function fetchSession(fetchImpl: typeof fetch = fetch): Promise<SessionState> {
  try {
    const res = await fetchImpl('/api/auth/session')
    const body = (await res.json()) as SessionState
    if (body?.status === 'ok') return { status: 'ok', user: body.user ?? null }
    if (body?.status === 'error') return body
    return { status: 'error', error: `unexpected response from /api/auth/session (${res.status})` }
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Asks for a sign-in link by email. The answer is the same whether or not the
 * address has an account — all it reports is that the message went out — so
 * this is not a way to ask who has an account here.
 */
export async function requestEmailSignIn(
  email: string,
  next = '/',
  fetchImpl: typeof fetch = fetch,
): Promise<EmailLinkState> {
  try {
    const res = await fetchImpl('/api/auth/email/start', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, next }),
    })
    const body = (await res.json()) as {
      status?: string
      error?: string
      email?: string
      expiresInMinutes?: number
    }
    if (body?.status === 'ok') {
      return {
        status: 'ok',
        email: body.email ?? email,
        expiresInMinutes: body.expiresInMinutes ?? 15,
      }
    }
    return { status: 'error', error: body?.error ?? `HTTP ${res.status}` }
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) }
  }
}

/** This browser only. */
export const signOut = (fetchImpl: typeof fetch = fetch) => post('/api/auth/signout', fetchImpl)

/** Every browser, including this one. */
export const signOutEverywhere = (fetchImpl: typeof fetch = fetch) =>
  post('/api/auth/signout-all', fetchImpl)

/** The `signin_error` the API redirects back with, if there is one. */
export function readSignInError(search: string): string | null {
  return new URLSearchParams(search).get('signin_error')
}

const MESSAGES: Record<string, string> = {
  access_denied: 'Sign-in was cancelled.',
  account_blocked: 'That account has been blocked. Get in touch if that looks wrong.',
  account_disabled: 'That account has been disabled. Get in touch if that looks wrong.',
  email_not_verified: 'Google has not verified that email address.',
  email_send_failed: 'We could not send that email just now. Please try again.',
  email_taken: 'Another FriendLoan account already uses that email address.',
  invalid_email: 'That does not look like an email address.',
  invalid_link:
    'That sign-in link has expired or has already been used. Ask for a new one.',
  invalid_state: 'That sign-in took too long. Please try again.',
  not_configured: 'Sign-in is not configured for this environment.',
  too_many_requests: 'That address has been sent enough links for now. Try again shortly.',
  unauthenticated: 'You are not signed in.',
}

export function describeSignInError(code: string): string {
  return MESSAGES[code] ?? `Sign-in failed (${code}). Please try again.`
}
